import { useRef, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { toast } from 'react-toastify';

// comps
import GlobalInput from '../global/input';
import { Box, Typography, Tooltip, CircularProgress, ClickAwayListener } from '@mui/material';

// constants
import { colors } from '../../theme';
import { MOMENT_DATE_DISPLAY_FORMAT } from '../../constants/date_format';

// icons
import { ReactComponent as UserIcon } from '../../assets/icons/user-icon.svg';
import { ReactComponent as GroupIcon } from '../../assets/icons/group-icon.svg';
import { ReactComponent as XIcon } from '../../assets/icons/x-icon.svg';

// api / redux
import { useAppSelector } from '../../hooks/hooks';
import TermListingApi from '../../api/term-listing.api';

const CRN_LENGTH = 5;
const TERMS_BACK = 2;
const TERMS_AHEAD = 2;

/*
 * Quick search is CRN only - no term, no campus, no school.
 * The user does not tell us the term, so we search the current term plus the
 * two before and the two after it. The existing /course-details/search endpoint
 * already supports a CRN lookup that ignores school and campus when it is called
 * in TermSchool mode, so one call per term in that window is all we need.
 */
const QuickSearch = (props) => {
    const { sx } = props;

    const termSchools = useAppSelector((state) => state.appState.termSchools);

    const [crn, setCrn] = useState('');
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // tracks the CRN of the newest request so slower responses can be ignored
    const latestRequest = useRef('');

    const _termListingApi = new TermListingApi();

    // current term + TERMS_BACK before it + TERMS_AHEAD after it, one entry per term code
    const getSearchTerms = () => {
        const uniqueTerms = [];

        [...(termSchools || [])]
            .sort((a, b) => moment(b.startDate).valueOf() - moment(a.startDate).valueOf())
            .forEach((term) => {
                if (!uniqueTerms.some((item) => item.code === term.code)) uniqueTerms.push(term);
            });

        // uniqueTerms runs newest -> oldest, so terms ahead sit before the current one
        const today = moment();
        let currentIndex = uniqueTerms.findIndex((term) => today.isBetween(moment(term.startDate), moment(term.endDate), 'day', '[]'));
        if (currentIndex === -1) currentIndex = uniqueTerms.findIndex((term) => moment(term.endDate).isBefore(today, 'day'));
        if (currentIndex === -1) currentIndex = 0;

        return uniqueTerms.slice(Math.max(0, currentIndex - TERMS_AHEAD), currentIndex + TERMS_BACK + 1);
    };

    const searchByCRN = async (value: string) => {
        latestRequest.current = value;
        setIsLoading(true);

        try {
            const terms = getSearchTerms();

            const results = await Promise.all(
                terms.map(async (term) => {
                    const { details } = await _termListingApi.GetTermListing('TermSchool', term.termSchoolId, null, parseInt(value, 10));
                    return (details || []).map((detail) => ({ ...detail, termCode: term.code }));
                })
            );

            // a slower request from an earlier CRN should not overwrite the current one
            if (latestRequest.current !== value) return;

            const merged = [];
            results.flat().forEach((detail) => {
                if (`${detail.crn}` !== value) return;
                if (merged.some((item) => item.rowId === detail.rowId)) return;
                merged.push(detail);
            });

            merged.sort((a, b) => moment(a.startDate).valueOf() - moment(b.startDate).valueOf());
            setRows(merged);
        } catch (error) {
            if (latestRequest.current === value) setRows([]);
            toast.error('Error searching for CRN');
        } finally {
            if (latestRequest.current === value) setIsLoading(false);
        }
    };

    const handleInputChange = (name: any, value: any) => {
        const digits = (value || '').replace(/\D/g, '').slice(0, CRN_LENGTH);

        setCrn(digits);
        setRows([]);

        if (digits.length === CRN_LENGTH) searchByCRN(digits);
        else latestRequest.current = digits;
    };

    const onClose = () => {
        latestRequest.current = '';
        setCrn('');
        setRows([]);
        setIsLoading(false);
    };

    // distinct, comma separated values for a column across every matching row
    const joinDistinct = (name: string) => {
        const values = [];

        rows.forEach((row) => {
            if (!row[name]) return;
            row[name]
                .split(',')
                .map((item) => item.trim())
                .forEach((item) => {
                    if (item && !values.includes(item)) values.push(item);
                });
        });

        return values.join(', ');
    };

    const course = rows?.length > 0 ? rows[0].course : null;
    const sets = joinDistinct('sets');
    const instructors = joinDistinct('instructors');

    return (
        <Box sx={sx}>
            <GlobalInput
                placeholder={'Quick Search by CRN (i.e. 48880)'}
                sx={{ width: '280px' }}
                type={'search'}
                onChange={handleInputChange}
                value={crn}
                inputProps={{ maxLength: CRN_LENGTH, inputMode: 'numeric' }}
            />
            {crn.length === CRN_LENGTH && (
                <ClickAwayListener onClickAway={onClose}>
                    <InfoContainer>
                        {isLoading || rows.length === 0 ? (
                            <FlexBox sx={{ minHeight: '200px', justifyContent: 'center' }}>
                                <FlexBox sx={{ flexDirection: 'column' }}>
                                    {isLoading ? <CircularProgress size={50} /> : <Typography sx={{ color: colors.grey.medium }}>No results found.</Typography>}
                                </FlexBox>
                            </FlexBox>
                        ) : (
                            <>
                                <FlexBox sx={{ justifyContent: 'space-between', svg: { cursor: 'pointer', path: { fill: colors.blue.main } } }}>
                                    <FlexBox>
                                        <CRN>{crn}</CRN>
                                        <Subject>
                                            {course?.subjectCode} {course?.number}
                                        </Subject>
                                        <Typography variant="body2" sx={{ color: colors.grey.medium }}>
                                            {course?.title}
                                        </Typography>
                                    </FlexBox>
                                    <XIcon onClick={onClose} />
                                </FlexBox>
                                <FlexBox>
                                    {sets && (
                                        <InfoBox>
                                            <UserIcon />
                                            <Typography variant="body2">{sets}</Typography>
                                        </InfoBox>
                                    )}
                                    {instructors && (
                                        <InfoBox>
                                            <GroupIcon />
                                            <Tooltip title={instructors.length > 75 ? instructors : ''} arrow>
                                                <Typography variant="body2" sx={{ cursor: 'default' }}>
                                                    {instructors.slice(0, 75)}
                                                    {instructors.length > 75 && '...'}
                                                </Typography>
                                            </Tooltip>
                                        </InfoBox>
                                    )}
                                </FlexBox>
                                <Table>
                                    <tbody>
                                        <tr>
                                            <th>Term</th>
                                            <th>Start Date</th>
                                            <th>End Date</th>
                                            <th style={{ textAlign: 'center' }}>Enrol / Cap</th>
                                            <th style={{ padding: '0px 10px' }}>Type</th>
                                            <th>Time</th>
                                            <th>Location</th>
                                            <th style={{ textAlign: 'center' }}>Day(s)</th>
                                            <th style={{ paddingRight: '0px' }}>Note</th>
                                        </tr>
                                        {rows.map((row) => {
                                            const { rowId, termCode, days, startDate, endDate, timeIn, timeOut, room, building, enrolled, capacity, sectionType, note } = row;
                                            return (
                                                <tr key={rowId}>
                                                    <td>{termCode}</td>
                                                    <td>{moment(startDate).format(MOMENT_DATE_DISPLAY_FORMAT)}</td>
                                                    <td>{moment(endDate).format(MOMENT_DATE_DISPLAY_FORMAT)}</td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        {enrolled}/{capacity}
                                                    </td>
                                                    <td style={{ padding: '0px 10px' }}>{sectionType?.name}</td>
                                                    <td>
                                                        {timeIn}- {timeOut}
                                                    </td>
                                                    <td>{building && room ? `${building}-${room}` : building ? building : room ? room : 'N/A'}</td>
                                                    <td style={{ textAlign: 'center' }}>{days}</td>
                                                    <td style={{ paddingRight: '0px' }}>{note}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </>
                        )}
                    </InfoContainer>
                </ClickAwayListener>
            )}
        </Box>
    );
};

export default QuickSearch;

const InfoContainer = styled.div`
    background-color: ${colors.white};
    min-width: 720px;
    max-width: 950px;
    min-height: 135px;
    max-height: 70vh;
    overflow-y: auto;
    padding: 12px;
    position: absolute;
    top: 82%;
    /* left: 0; */
    border-radius: 5px;
    box-shadow: ${colors.boxShadow};
    color: ${colors.black};
`;

const FlexBox = styled(Box)`
    && {
        display: flex;
        align-items: center;
    }
`;

const CRN = styled.span`
    padding: 4px;
    border-radius: 4px;
    background-color: ${colors.blue.light};
    font-weight: 700;
    color: ${colors.blue.main};
    line-height: 1.25;
    margin-right: 8px;
`;

const Subject = styled(Typography)`
    && {
        font-weight: 700;
        color: ${colors.blue.main};
        font-size: 16px;
        margin-right: 8px;
    }
`;

const InfoBox = styled(Box)`
    && {
        display: flex;
        align-items: center;
        margin-right: 4px;
        padding: 4px;
        svg {
            width: 16px;
            height: auto;
            margin-right: 4px;
            path {
                fill: ${colors.black};
            }
        }
        p {
            line-height: 1;
        }
    }
`;

const Table = styled.table`
    width: 100%;
    background-color: ${colors.grey.light};
    border-radius: 4px;
    padding: 4px 8px;
    th {
        text-align: left;
        font-size: 12px;
        font-weight: 600;
        color: ${colors.grey.medium};
        padding-right: 1em;
    }
    td {
        padding-right: 1em;
        /* font-size: 12px; */
    }
`;
